import React from "react";

export interface CartItem {
  id: string;
  name: string;
  brand: string;
  price: number;
  size: string;
  image?: string;
  quantity: number;
  sellerId?: string;
  condition?: string;
  /** New Drops line — paid to District88 (platform Stripe), not a seller Connect account. */
  kind?: 'listing' | 'drop';
  /** Server verifies price against this drop row when `kind === 'drop'`. */
  dropId?: string;
}

export interface CartContextType {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  /** Pass `size` when the same listing/drop id can appear on multiple lines (different sizes). */
  removeItem: (id: string, size?: string) => void;
  updateQuantity: (id: string, quantity: number, size?: string) => void;
  clearCart: () => void;
  getTotalPrice: () => number;
  itemCount: number;
}

const CART_KEY = 'district88_cart';

function loadCart(): CartItem[] {
  try {
    const saved = localStorage.getItem(CART_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

function saveCart(items: CartItem[]) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  } catch {
    // localStorage full or unavailable
  }
}

export const CartContext = React.createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = React.useState<CartItem[]>(loadCart);

  // Persist to localStorage whenever items change
  React.useEffect(() => {
    saveCart(items);
  }, [items]);

  const addItem = (item: CartItem) => {
    setItems(prev => {
      const existingItem = prev.find((i) => i.id === item.id && i.size === item.size);
      if (existingItem) {
        return prev.map((i) =>
          i.id === item.id && i.size === item.size ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...item, quantity: item.quantity || 1 }];
    });
  };

  const removeItem = (id: string, size?: string) => {
    setItems((prev) =>
      prev.filter((i) => (size !== undefined ? !(i.id === id && i.size === size) : i.id !== id))
    );
  };

  const updateQuantity = (id: string, quantity: number, size?: string) => {
    if (quantity <= 0) {
      removeItem(id, size);
      return;
    }
    setItems((prev) =>
      prev.map((i) =>
        size !== undefined
          ? i.id === id && i.size === size
            ? { ...i, quantity }
            : i
          : i.id === id
            ? { ...i, quantity }
            : i
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, getTotalPrice, itemCount }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = React.useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};
